import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

const log = debug('rois:components:modals:download-historic');

if (import.meta.env.DEV) {
  debug.enable('rois:components:modals:download-historic*');
}

// Styles
import {
  Button,
  ButtonsRow,
  Container,
  Icon,
  Title,
  GeneratingBox,
  GeneratingText,
  SortControl,
  SortOptionsRow,
  SortOptionButton,
  SortArrow,
  SortHint,
} from './styles';

// Types
import { ModalProps } from './types';
import { OrderType } from '../../../../types/orders';

//Components
import ModalContainer from '../../containers/ModalContainer';
import { Loader } from '../../../inputs/Loader';
import { Typography } from '../../../display/Typography';

// Icons
import DownloadIcon from '../../../../assets/icons/download-icon.svg';
import { ArrowBottom } from '../../../../assets/icons/ArrowBottom';

// Excel
import { TableToExcel } from '../../../../utils/table-to-excel';
import {
  detectCompositeKeyColumns,
  readCompositeKeysFromDom,
  readRoidsFromDom,
} from './domExportKeys';
import { generateExcelFromData } from '../../../../utils/excel-from-data';
import { resolveImageUrl } from '../../../../utils/cloudinary';
import {
  isSketchName,
  MAX_GRID_IMAGES,
  type ReportImage,
} from '../../../../utils/pdf/reportImages';
import {
  createPhotoLocationContext,
  photoLocationLines,
} from '../../../../utils/photoLocation';
import { EMPTY_VALUE } from '../../../../constants/emptyValue';

// Selectors for resolving raw data
import { selectActionOrders } from '../../../../gql/selectors/actions';
import { selectAccidentOrders } from '../../../../gql/selectors/accidents';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { selectCommunicationOrders } from '../../../../gql/selectors/communications';

// Queries for fetching all records
import { FETCH_ACTIONS, GET_ACTION_BY_ID } from '../../../../gql/queries/actions/actions';
import { FETCH_ACCIDENTS, GET_ACCIDENT_BY_ID } from '../../../../gql/queries/accidents/accidents';
import { GET_GENERIC_ORDERS, GET_GENERIC_ORDER_BY_ID } from '../../../../gql/queries/genericOrders';
import { GET_CAMPAIGNS, GET_CAMPAIGN_BY_ID } from '../../../../gql/queries/campaign';
import { FETCH_COMMUNICATIONS, GET_COMMUNICATION_BY_ID } from '../../../../gql/queries/communication';
import { GET_ACTION_BUDGET_ITEMS_HISTORICAL } from '../../../../gql/queries/budgets/actionBudgetItems';
import {
  GET_SURVEILLANCE_ROUTES,
  GET_SURVEILLANCE_ROUTE_BY_ID,
} from '../../../../gql/queries/surveillanceRoute';
import { FETCH_TEAMS } from '../../../../gql/queries/users';
import { GET_ROADS } from '../../../../gql/queries/geoPositions/roads';
import { selectOrdersForSurveillanceRoutes } from '../../../../gql/selectors/surveillanceRoute';
import {
  buildRoidFallback,
  mapRouteStateToTagVariant,
  resolveRouteRoadRanges,
} from '../../../../utils/surveillanceRoute';

/** Un recorrido que cubre más de una carretera no tiene «un» rango de PK. */
const srIsMultiRoad = (sr: { roadIDs?: string[] | null }): boolean =>
  (sr.roadIDs?.length ?? 0) > 1;
// Tour P2 (2026-07-06): Informe PDF del RV desde este modal (spec §Informe PDF).

// PDF
import { TableToPDF } from '../../../../utils/table-to-pdf';
import { generateHistoricalPDF } from '../../../../utils/pdf/generateHistoricalPDF';
import { generateDetailedPDF } from '../../../../utils/pdf/generateDetailedPDF';
import { fetchDetailedReportData } from '../../../../utils/pdf/fetchReportData';
import { generateCombinedPDF } from '../../../../utils/pdf/generateCombinedPDF';
// Informe del RV (2026-07-27): documento propio con opciones por niveles — el
// RV ya NO pasa por el informe combinado.
import { generateSurveillancePDF } from '../../../../utils/pdf/generateSurveillancePDF';
import { SurveillanceReportOptions } from './SurveillanceReportOptions';
import {
  SurveillanceReportOptions as SurveillanceReportOptionsType,
  SR_REPORT_PRESET_SUMMARY,
} from '../../../../utils/pdf/surveillanceTypes';
import {
  ReportSortField,
  ReportSortOption,
  DEFAULT_REPORT_SORT,
} from '../../../../utils/pdf/combinedSort';
import { HistoricalReportData, PDFRecord } from '../../../../utils/pdf/types';

// Multimedia
import { selectImagesByMultipleOrderIDs } from '../../../../gql/selectors/multimedia';

// Graphql
import { client } from '../../../../gql/client';
import { GET_ORDERS_BY_IDS, GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';
import { GET_ACTIONS_BY_IDS } from '../../../../gql/queries/actions/actions';
import { GET_ACTION_BUDGET_ITEM_BY_ID } from '../../../../gql/queries/budgets/actionBudgetItems';
import { GET_CONCESSION_BY_ID } from '../../../../gql/queries/geoPositions/roads';
import { FETCH_PREDEFINED_ORDERS } from '../../../../gql/queries/orders/predefinedData';
import i18n from '../../../../i18n';

// Utils
import { formatRoidWithDots } from '../../../display/tables/utils/roidFormatter';
import { format } from 'date-fns';

// Types
import { Order } from '../../../../types/orders';
import { useTheme } from 'styled-components';

export const DownloadHistoricModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['modals', 'tables', 'common', 'toasts', 'budgets']);
  const theme = useTheme();
  // Bug #1: el estado debe salir traducido en el Excel (mismo mapa que SpecialTag).
  const translateState = (state?: string | null): string =>
    state ? (t(`common:states.${state}`) as string) : '';
  console.log('[MODAL] DownloadHistoricModal RENDER — data received:', {
    type: data.type,
    selectAll: data.selectAll,
    filtersKeys: data.filters ? Object.keys(data.filters) : 'NO FILTERS',
    selectedRowsCount: data.selectedRows?.length,
    excludedIdsCount: data.excludedIds?.length,
    totalCount: data.totalCount,
    showReport: data.showReport,
  });
  const tableToExcelRef = useRef<TableToExcel | null>(null);
  const tableToPdfRef = useRef<TableToPDF | null>(null);
  const pdfDataRef = useRef<HistoricalReportData | null>(null);

  // Indicador de generación: spinner + progreso "X/Y" mientras se construye el
  // histórico (Excel) o el informe (PDF detallado / combinado). El informe
  // detallado y el combinado emiten progreso real vía onProgress; el Excel solo
  // muestra el spinner.
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  // Orden de las entidades internas (actuaciones/comunicaciones/accidentes)
  // del informe combinado. Estado local del modal: se resetea en cada apertura.
  const [reportSort, setReportSort] =
    useState<ReportSortOption>(DEFAULT_REPORT_SORT);

  // Informe del Recorregut de Vigilància: qué se pinta. Arranca SIEMPRE en el
  // preset Resumen (la ficha + la tabla de tramos tal y como se ve en
  // pantalla); el usuario escala desde ahí.
  const isSurveillanceReport = data.entityType === 'surveillanceRoute';
  const [srOptions, setSrOptions] = useState<SurveillanceReportOptionsType>(
    SR_REPORT_PRESET_SUMMARY,
  );
  // El orden de las hijas solo importa si el informe las incluye.
  const showSortControl =
    !!data.showCombinedReport &&
    (!isSurveillanceReport || srOptions.children !== 'none');

  const onToggleReportSort = useCallback((field: ReportSortField) => {
    setReportSort((prev) => ({
      field,
      // Mismo criterio que SortableHeader en las tablas: repetir el campo
      // activo alterna la dirección; un campo nuevo entra en descendente.
      direction:
        prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const runWithSpinner = useCallback(
    async (fn: () => Promise<void>): Promise<void> => {
      setGenerating(true);
      setProgress(null);
      try {
        await fn();
      } finally {
        setGenerating(false);
        setProgress(null);
      }
    },
    [],
  );

  useEffect(() => {
    log('🔍 Debug Info:');
    log('Selected rows:', data.selectedRows);
    log('Data type:', data.type);
    log('Filter position column:', data.filterPositionColumn);

    const getFilterValuesFromData = async () => {
      let filterValues: string[] = [];

      // Handle different data types
      if (data.type === 'Action') {
        log('⚡ Processing Actions...');

        try {
          const result = client.readQuery({
            query: GET_ACTIONS_BY_IDS,
            variables: {
              ids: data.selectedRows,
            },
          });

          log('📦 Cache query result for actions:', result);

          if (result && result.readActions) {
            log('📋 Actions from cache:', result.readActions);

            filterValues = result.readActions.map((action: any) => {
              const formattedRoid = action.roid;
              log(
                `🏷️ Action ${action.actionID}: ROID ${action.roid} -> formatted: ${formattedRoid}`
              );
              return formattedRoid;
            });

            log('✅ Got ROIDs from actions cache:', filterValues);
          } else {
            log('⚠️ No data found in cache for selected actions');
          }
        } catch (error) {
          console.error('💥 Error reading actions from cache:', error);
        }
      } else if (data.type === 'ActionBudgetItems') {
        log('💰 Processing ActionBudgetItems...');

        try {
          // For ActionBudgetItems, get composite keys (Budget Item Code + ROID) from individual queries
          const compositeKeyPromises = data.selectedRows.map(async (id) => {
            try {
              const result = await client.query({
                query: GET_ACTION_BUDGET_ITEM_BY_ID,
                variables: { id },
                fetchPolicy: 'cache-first',
              });

              const budgetItemCode =
                result.data?.actionBudgetItem?.budgetItem?.code;
              const roid = result.data?.actionBudgetItem?.action?.roid;

              if (budgetItemCode && roid) {
                const formattedRoid = formatRoidWithDots(roid);
                const compositeKey = `${budgetItemCode}|${formattedRoid}`;
                log(
                  `💰 ActionBudgetItem ${id}: Code ${budgetItemCode} + ROID ${formattedRoid} = ${compositeKey}`
                );
                return compositeKey;
              }
              return null;
            } catch (error) {
              console.error(`💥 Error fetching ActionBudgetItem ${id}:`, error);
              return null;
            }
          });

          const resolvedKeys = await Promise.all(compositeKeyPromises);
          filterValues = resolvedKeys.filter(
            (key): key is string => key !== null
          );
          log(
            '✅ Got Composite Keys from ActionBudgetItems:',
            filterValues
          );
        } catch (error) {
          console.error('💥 Error processing ActionBudgetItems:', error);
        }
      } else {
        log('📞 Processing Communication Orders...');

        try {
          const result = client.readQuery({
            query: GET_ORDERS_BY_IDS,
            variables: {
              ids: data.selectedRows,
            },
          });

          log('📦 Cache query result:', result);

          if (result && result.readOrders) {
            log('📋 Orders from cache:', result.readOrders);

            filterValues = result.readOrders.map((order: Order) => {
              const formattedRoid = formatRoidWithDots(order.roid);
              log(
                `🏷️ Order ${order.id}: ROID ${order.roid} -> formatted: ${formattedRoid}`
              );
              return formattedRoid;
            });

            log('✅ Got ROIDs from cache:', filterValues);
          } else {
            log('⚠️ No data found in cache for selected orders');
          }
        } catch (error) {
          console.error('💥 Error reading orders from cache:', error);
        }
      }

      // Detect composite key column indices from the DOM table headers
      let compositeKeyColumns: number[] | undefined;
      const tableElement = document.querySelector('#historical-' + data.type);

      if (data.type === 'ActionBudgetItems' && tableElement) {
        compositeKeyColumns = detectCompositeKeyColumns(tableElement);
        log('Detected compositeKeyColumns:', compositeKeyColumns);
      }

      // If we couldn't get filter values from cache/queries, try DOM extraction
      if (filterValues.length === 0) {
        log(
          'Attempting alternative approach: reading from DOM table'
        );

        if (tableElement) {
          if (data.type === 'ActionBudgetItems' && compositeKeyColumns) {
            // For budget items, build composite keys from DOM
            filterValues = readCompositeKeysFromDom(
              tableElement,
              compositeKeyColumns
            );
          } else {
            // Auto-detect ROID column by looking at headers
            const { roidColumnIndex, values } = readRoidsFromDom(
              tableElement,
              data.filterPositionColumn || 1
            );
            log(`Using ROID column index: ${roidColumnIndex}`);
            filterValues = values;

            // Update the filter position column for TableToExcel
            data.filterPositionColumn = roidColumnIndex;
          }

          log('Filter values extracted from DOM:', filterValues);
        }
      }

      log('Final filter values for filtering:', filterValues);

      // Build TableToExcel options
      const excelOptions: Record<string, unknown> = {
        filename: `historical-${data.type}.xlsx`,
        ignoreColumns: [0],
        filterByIDs: filterValues,
      };

      if (!isNaN(data.filterPositionColumn)) {
        excelOptions.filterPositionColumn = data.filterPositionColumn;
      }

      if (compositeKeyColumns) {
        excelOptions.compositeKeyColumns = compositeKeyColumns;
      }

      // Initialize TableToExcel
      tableToExcelRef.current = new TableToExcel(
        '#historical-' + data.type,
        excelOptions
      );

      log('TableToExcel initialized with config:', {
        selector: '#historical-' + data.type,
        ...excelOptions,
      });

      // Initialize TableToPDF with same filter options
      const pdfOptions: Record<string, unknown> = {
        ...excelOptions,
        filename: `historical-${data.type}.pdf`,
        title: t('downloadHistoric.title'),
        orientation: 'landscape' as const,
      };

      tableToPdfRef.current = new TableToPDF(
        '#historical-' + data.type,
        pdfOptions
      );

      log('TableToPDF initialized with config:', {
        selector: '#historical-' + data.type,
        ...pdfOptions,
      });

      // Build rich PDF data
      try {
        const tableData = tableToPdfRef.current.getData();

        let imagesByRoid: Record<string, ReportImage[]> = {};
        // Los Recorridos quedan fuera: sus filas llevan el id del
        // SurveillanceRoute, no el de la Order gemela, así que preguntar por
        // imágenes con esos ids no devuelve nada y solo llenaba la consola de
        // errores. Su informe trae las fotos por su propio camino
        // (`SurveillanceReport`), no por `imagesByRoid`.
        if (
          data.type !== 'ActionBudgetItems' &&
          data.entityType !== 'surveillanceRoute' &&
          filterValues.length > 0
        ) {
          try {
            const images = await selectImagesByMultipleOrderIDs(data.selectedRows);
            // Dónde se hizo cada foto (spec fotos geolocalizadas § 19). Solo de
            // las que el informe pinta (`MAX_IMAGES` por registro): un histórico
            // puede traer cientos, y cada una son 128 KB hasta que el servidor
            // guarde la ubicación (§ 18). Sin el flag, `null` y nada que leer.
            const locationCtx = await createPhotoLocationContext(client, (key, options) =>
              t(key, options) as string,
            );
            const pending: Promise<void>[] = [];
            images.forEach((item) => {
              const roid = Array.isArray(item.roid)
                ? item.roid.join('.')
                : (item.roid as string);
              const resolvedUrl = roid ? resolveImageUrl(item.image, 'full') : null;
              if (roid && resolvedUrl) {
                if (!imagesByRoid[roid]) imagesByRoid[roid] = [];
                const entry: ReportImage = {
                  url: resolvedUrl,
                  name: item.image?.name || '',
                };
                if (
                  locationCtx &&
                  imagesByRoid[roid].length < MAX_GRID_IMAGES &&
                  !isSketchName(entry.name)
                ) {
                  pending.push(
                    photoLocationLines(item.image, locationCtx).then((location) => {
                      if (location) entry.location = location;
                    }),
                  );
                }
                imagesByRoid[roid].push(entry);
              }
            });
            await Promise.all(pending);
          } catch { /* continue without images */ }
        }

        const records: PDFRecord[] = tableData.rows.map((row, rowIdx) => ({
          roid: filterValues[rowIdx] || '',
          fields: tableData.headers.map((label, i) => ({
            label,
            // Una celda de varias líneas viaja como array desde `TableToPDF`
            // (autoTable pinta una línea por elemento). El informe rico es
            // react-pdf, que respeta los saltos dentro de un `<Text>`, así que
            // aquí se vuelven a unir con `\n` y se leen igual.
            value: Array.isArray(row[i]) ? (row[i] as string[]).join('\n') : (row[i] as string) || '',
          })).filter((_, i) => i > 0),
          images: imagesByRoid[filterValues[rowIdx]] || [],
        }));

        pdfDataRef.current = {
          title: t('downloadHistoric.title'),
          orderType: data.type,
          records,
          generatedAt: new Date().toLocaleDateString('ca-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }),
          totalCount: records.length,
        };

        log('Rich PDF data built with', records.length, 'records');
      } catch (error) {
        log('Error building rich PDF data:', error);
      }
    };

    getFilterValuesFromData();
  }, [data.selectedRows]);

  const downloadHistoric = useCallback(async () => {
    const isAccident = data.type === 'Accident' || data.type === 'accident';
    const isAction = data.type === 'Action' || data.type === 'action';
    const isGeneric = data.type === OrderType.Generic || data.type === OrderType.Campaign;
    const isCommunication = data.type === OrderType.Communication;
    const isBudgetItem = data.type === 'ActionBudgetItems';
    // FR-2.5 (2026-07-03): Excel de històrics del Recorregut de Vigilància.
    // Sin esta rama, el tipo caía al fallback DOM (TableToExcel sin wiring en
    // la página SR) → no-op silencioso con el modal colgado.
    const isSurveillanceRoute = data.type === OrderType.SurveillanceRoute;
    const canDataExport = (isAccident || isAction || isGeneric || isCommunication || isBudgetItem || isSurveillanceRoute) && data.filters;

    console.log('[MODAL] downloadHistoric called');
    console.log('[MODAL] data.type:', data.type, '| canDataExport:', canDataExport);
    console.log('[MODAL] data.selectAll:', data.selectAll);
    console.log('[MODAL] data.filters:', JSON.stringify(data.filters));

    // DATA-BASED EXPORT for all types with filters (supports multi-page)
    if (canDataExport) {
      console.log('[MODAL] Entering data-based export branch');
      try {
        // Helper formatters
        const roidFmt = (roid: any) =>
          Array.isArray(roid) ? roid.join('.') : typeof roid === 'string' ? roid : '';
        const dateFmt = (d: any) => {
          if (!d) return '';
          try {
            return new Date(d).toLocaleDateString('ca-ES', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            });
          } catch { return String(d); }
        };
        const pkFmt = (km: any, m: any) => {
          if (km == null && m == null) return '';
          return `${km || 0} + ${String(m || 0).padStart(3, '0')} km`;
        };

        let resolvedRecords: any[];
        // Definición ÚNICA de columnas por tipo: cabecera y dato viajan juntos.
        //
        // Antes esto eran DOS arrays paralelos (un `rows` por rama + un switch
        // de `headers` 250 líneas más abajo) que había que mantener alineados
        // contando posiciones a mano. Divergieron: la columna con cabecera
        // "Fecha de registro" acabó pintando `creationDate || registerInitDate`
        // (la del sistema) — bug reportado desde producción en jun-2026. Con
        // `{ header, value }` co-localizados esa clase de desalineación es
        // imposible por construcción.
        //
        // Criterio de contenido: el Excel es un SUPERCONJUNTO de la tabla en
        // pantalla — mismo orden de columnas que la tabla, nunca se quita una
        // columna que ya se exportaba. Cada `value` usa solo campos que el
        // selector de la fila ya devuelve (si no, la columna saldría vacía).
        let columns: { header: string; value: (r: any) => string }[];

        // ── Traer del backend SOLO lo seleccionado + progreso 1→X (espejo del "Informe").
        // ANTES: siempre `take:99999` (toda la lista del filtro) + `selector(all)`
        // (tormenta N+1: un GetOrderById por registro) y filtrar después.
        // AHORA: selección individual → se piden ÚNICAMENTE los registros marcados
        // por id (Order → entidad hija → entidad por id, mismo fragment que la lista),
        // en lotes con progreso. `selectAll` sí trae la lista (necesita "todos menos
        // los excluidos"); el filtro por `excludedIds` se aplica después.
        const isIndividual = !data.selectAll;
        const selectedRowIds = (data.selectedRows || []).filter(Boolean) as string[];
        const CHUNK = 25;

        // Resuelve registros raw en lotes (acota concurrencia + progreso 1→X).
        const resolveInChunks = async (
          records: any[],
          selectorFn: (chunk: any[]) => Promise<any[]>,
        ): Promise<any[]> => {
          if (!records.length) return [];
          const out: any[] = [];
          setProgress({ current: 0, total: records.length });
          for (let i = 0; i < records.length; i += CHUNK) {
            const part = await selectorFn(records.slice(i, i + CHUNK));
            out.push(...(part || []));
            setProgress({
              current: Math.min(i + CHUNK, records.length),
              total: records.length,
            });
          }
          return out;
        };

        // Selección individual: `data.selectedRows` = ids de Order. Para cada uno,
        // Order(id) → id de su entidad hija → entidad hija por id (raw para el selector).
        // En lotes, con progreso. Evita pedir la lista completa del filtro.
        const fetchSelectedChildRaws = async (
          childIdField: string,
          childByIdQuery: any,
          childKey: string,
        ): Promise<any[]> => {
          if (!selectedRowIds.length) return [];
          const out: any[] = [];
          setProgress({ current: 0, total: selectedRowIds.length });
          for (let i = 0; i < selectedRowIds.length; i += CHUNK) {
            const chunk = selectedRowIds.slice(i, i + CHUNK);
            const raws = await Promise.all(
              chunk.map(async (orderId) => {
                try {
                  const { data: od } = await client.query({
                    query: GET_ORDER_BY_ID,
                    variables: { id: orderId },
                    fetchPolicy: 'cache-first',
                  });
                  const childId = od?.order?.[childIdField]?.[0];
                  if (!childId) return null;
                  const { data: cd } = await client.query({
                    query: childByIdQuery,
                    variables: { id: childId },
                    fetchPolicy: 'cache-first',
                  });
                  return cd?.[childKey] ?? null;
                } catch {
                  return null;
                }
              }),
            );
            out.push(...raws.filter(Boolean));
            setProgress({
              current: Math.min(i + CHUNK, selectedRowIds.length),
              total: selectedRowIds.length,
            });
          }
          return out;
        };

        if (isAction || isAccident) {
          // --- Actions / Accidents ---
          let rawRecords: any[];
          if (isIndividual) {
            rawRecords = await fetchSelectedChildRaws(
              isAccident ? 'accidentIDs' : 'actionIDs',
              isAccident ? GET_ACCIDENT_BY_ID : GET_ACTION_BY_ID,
              isAccident ? 'accident' : 'action',
            );
          } else {
            const query = isAccident ? FETCH_ACCIDENTS : FETCH_ACTIONS;
            const key = isAccident ? 'accidents' : 'actions';
            const result = await client.query({
              query,
              variables: { input: { ...data.filters, take: 99999, cursor: '' } },
              fetchPolicy: 'no-cache',
            });
            rawRecords = result.data?.[key]?.[key] || [];
          }
          const selector = isAccident ? selectAccidentOrders : selectActionOrders;
          resolvedRecords = await resolveInChunks(rawRecords, selector);

          const laneCutFmt = (r: any) =>
            r.isLaneCut ? t('forms:actionEdition.yes') : t('forms:actionEdition.no');
          // "Origen": mismo mapa de labels que la celda de la tabla
          // (`common:orderTypes.*`), y vacío si el dato no está — que es lo que
          // pasa en producción hasta que corra el backfill de containerType.
          const originFmt = (r: any) =>
            r.containerType ? t(`common:orderTypes.${r.containerType}`) : '';

          if (isAction) {
            // Orden y contenido de `ValidationsTable` (la tabla de actuaciones,
            // que los históricos reusan), incluida la columna "Origen".
            //
            // "Origen" (`containerType`) SÍ va en Actuación y Accidente, y no
            // en genéricas/campañas: estas dos cuelgan de un padre y fuera de
            // la aplicación no hay forma cómoda de saber de cuál, mientras que
            // una genérica o una campaña SON el contenedor — su origen sería
            // siempre vacío. (Comunicaciones tampoco la lleva: su tabla no
            // tiene esa columna.)
            //
            // En la tabla la columna se oculta en las pestañas contextuales
            // (dentro de una campaña o de un recorrido), donde todas las filas
            // comparten origen. En el fichero se exporta igualmente: un Excel
            // se abre lejos de la pantalla que lo generó, así que ese dato
            // repetido documenta de dónde salieron las filas en vez de estorbar.
            //
            // 🔴 Al desplegar: `containerType` se rellenó en beta pero el
            // backfill NUNCA corrió en producción (medido: 0 de 15.334). Hasta
            // que corra `scripts/backfill-container-fields.ts`, allí la columna
            // saldrá vacía en todo lo histórico.
            columns = [
              { header: t('common:tables.headers.state'), value: (r) => translateState(r.state) },
              { header: t('common:tables.headers.roid'), value: (r) => roidFmt(r.roid) },
              { header: t('forms:workOrder.startDate'), value: (r) => dateFmt(r.registerInitDate) },
              { header: t('forms:workOrder.endDate'), value: (r) => dateFmt(r.registerEndDate) },
              { header: t('forms:workOrder.arrivalDate'), value: (r) => dateFmt(r.registerArrivalDate) },
              { header: t('common:tables.headers.capitol'), value: (r) => r.capitol || '' },
              { header: t('common:tables.headers.subCapitol'), value: (r) => r.subCapitol || '' },
              { header: t('forms:workOrder.predefined'), value: (r) => r.predefinedOrder || '' },
              { header: t('common:tables.headers.concession'), value: (r) => r.concession || '' },
              { header: t('common:tables.headers.road'), value: (r) => r.road || '' },
              // Sentido: el valor lo formatea `selectActionOrders` con el helper
              // canónico. Misma clave de cabecera que el bloque de accidentes,
              // para que las dos hojas se lean igual.
              { header: t('common:workOrder.direction'), value: (r) => r.direction || '' },
              { header: t('forms:workOrder.roadType'), value: (r) => r.track || '' },
              // Carril, traducido por catálogo en el mismo selector.
              { header: t('tables:headers.lane'), value: (r) => r.lane || '' },
              { header: t('common:labels.creator'), value: (r) => r.user || '' },
              { header: t('forms:registeredBy.label'), value: (r) => r.registeredBy || '' },
              // La básica del RV no tiene tripulación (es del recorrido): guion.
              { header: t('common:tabs.operators'), value: (r) => (r.isBasicAction ? EMPTY_VALUE : r.operators || '') },
              { header: t('forms:workOrder.action'), value: (r) => r.activity || '' },
              { header: t('common:tables.headers.laneCut'), value: laneCutFmt },
              { header: t('common:tables.headers.pkInit'), value: (r) => r.pkInit || '' },
              { header: t('common:tables.headers.pkEnd'), value: (r) => r.pkEnd || '' },
              { header: t('common:tables.headers.description'), value: (r) => r.description || '' },
              {
                header: t('common:tables.headers.externalCompanies'),
                value: (r) => (r.externalCompanies || []).join(', '),
              },
              { header: t('common:tables.headers.origin'), value: originFmt },
              { header: t('common:tables.headers.creationDate'), value: (r) => dateFmt(r.creationDate) },
            ];
          } else {
            // Orden y contenido de `AccidentsTable`, "Origen" incluido (ver arriba).
            columns = [
              { header: t('common:tables.headers.state'), value: (r) => translateState(r.state) },
              { header: t('common:tables.headers.roid'), value: (r) => roidFmt(r.roid) },
              {
                header: t('common:tables.headers.registerInitDate'),
                value: (r) => dateFmt(r.registerInitDate),
              },
              { header: t('common:tables.headers.mediumKnowledge'), value: (r) => r.mediumKnowledge || '' },
              { header: t('common:tables.headers.weather'), value: (r) => r.weather || '' },
              { header: t('common:tables.headers.laneCut'), value: laneCutFmt },
              { header: t('forms:workOrder.predefined'), value: (r) => r.predefinedOrder || '' },
              { header: t('common:tables.headers.concession'), value: (r) => r.concession || '' },
              { header: t('common:tables.headers.road'), value: (r) => r.road || '' },
              // Sentido: el valor lo formatea `selectAccidentOrders` con el
              // helper canónico compartido con genéricas/campañas.
              { header: t('common:workOrder.direction'), value: (r) => r.direction || '' },
              { header: t('common:tables.headers.pkInit'), value: (r) => r.pkInit || '' },
              { header: t('common:tables.headers.pkEnd'), value: (r) => r.pkEnd || '' },
              { header: t('common:labels.creator'), value: (r) => r.user || '' },
              { header: t('forms:registeredBy.label'), value: (r) => r.registeredBy || '' },
              { header: t('common:tables.headers.description'), value: (r) => r.description || '' },
              { header: t('common:tables.headers.origin'), value: originFmt },
              { header: t('common:tables.headers.creationDate'), value: (r) => dateFmt(r.creationDate) },
            ];
          }
        } else if (isGeneric) {
          // --- Generic Orders / Campaigns ---
          // `isGeneric` cubre Generic Y Campaign (mismas columnas), pero cada uno
          // tiene su propia query/colección. Sin esto, campañas consultaba
          // genéricas → ningún id coincidía → Excel vacío.
          const isCampaign = data.type === OrderType.Campaign;
          let rawRecords: any[];
          if (isIndividual) {
            rawRecords = await fetchSelectedChildRaws(
              isCampaign ? 'campaignIDs' : 'genericOrdersIDs',
              isCampaign ? GET_CAMPAIGN_BY_ID : GET_GENERIC_ORDER_BY_ID,
              isCampaign ? 'campaign' : 'genericOrder',
            );
          } else {
            const result = await client.query({
              query: isCampaign ? GET_CAMPAIGNS : GET_GENERIC_ORDERS,
              variables: { input: { ...data.filters, take: 99999, cursor: '' } },
              fetchPolicy: 'no-cache',
            });
            rawRecords = isCampaign
              ? result.data?.campaigns?.campaigns || []
              : result.data?.genericOrders?.genericOrders || [];
          }
          resolvedRecords = await resolveInChunks(rawRecords, selectAllOrders);

          // Genéricas y campañas comparten query-shape pero NO tabla: campañas
          // se pintan con `CampaignTable`, que tiene "Nom de la campanya" (y
          // por eso el Excel de campañas la perdía al compartir columnas).
          columns = [
            { header: t('common:tables.headers.state'), value: (r) => translateState(r.state) },
            { header: t('common:tables.headers.roid'), value: (r) => roidFmt(r.roid) },
            {
              header: t('common:tables.headers.registerInitDate'),
              value: (r) => dateFmt(r.registerInitDate),
            },
            { header: t('forms:workOrder.endDate'), value: (r) => dateFmt(r.derivedEndDate) },
            ...(isCampaign
              ? [{ header: t('common:tables.headers.name'), value: (r: any) => r.name || '' }]
              : [
                  { header: t('common:tables.headers.capitol'), value: (r: any) => r.capitol || '' },
                  {
                    header: t('common:tables.headers.subCapitol'),
                    value: (r: any) => r.subCapitol || '',
                  },
                ]),
            { header: t('forms:workOrder.predefined'), value: (r) => r.predefinedOrder || '' },
            { header: t('common:tables.headers.team'), value: (r) => r.team || '' },
            { header: t('common:tables.headers.concession'), value: (r) => r.concession || '' },
            { header: t('common:tables.headers.road'), value: (r) => r.road || '' },
            { header: t('forms:workOrder.roadType'), value: (r) => r.track || '' },
            { header: t('common:workOrder.direction'), value: (r) => r.direction || '' },
            { header: t('common:tables.headers.pkInit'), value: (r) => r.pkInit || '' },
            { header: t('common:tables.headers.pkEnd'), value: (r) => r.pkEnd || '' },
            { header: t('common:labels.creator'), value: (r) => r.user || '' },
            { header: t('common:tables.headers.description'), value: (r) => r.description || '' },
            { header: t('common:tables.headers.creationDate'), value: (r) => dateFmt(r.creationDate) },
            // Sin "Registrado por": este bloque cubre genéricas Y campañas, y
            // ese dato solo se pide al crear Actuación o Accidente → la columna
            // saldría siempre vacía.
          ];
        } else if (isCommunication) {
          // --- Communications ---
          let rawRecords: any[];
          if (isIndividual) {
            rawRecords = await fetchSelectedChildRaws(
              'communicationIDs',
              GET_COMMUNICATION_BY_ID,
              'communication',
            );
          } else {
            const result = await client.query({
              query: FETCH_COMMUNICATIONS,
              variables: { input: { ...data.filters, take: 99999, cursor: '' } },
              fetchPolicy: 'no-cache',
            });
            rawRecords = result.data?.communications?.communications || [];
          }
          resolvedRecords = await resolveInChunks(rawRecords, selectCommunicationOrders);

          // Orden y contenido de `CheckBoxArrowTable` (la tabla de
          // comunicaciones que reusan los históricos).
          columns = [
            { header: t('common:tables.headers.state'), value: (r) => translateState(r.state) },
            { header: t('common:tables.headers.roid'), value: (r) => roidFmt(r.roid) },
            {
              header: t('common:tables.headers.registerInitDate'),
              value: (r) => dateFmt(r.registerInitDate),
            },
            { header: t('common:tables.headers.groupInter'), value: (r) => r.groupInter || '' },
            { header: t('common:tables.headers.media'), value: (r) => r.media || '' },
            { header: t('common:tables.headers.sendStatus'), value: (r) => r.sendStatus || '' },
            { header: t('forms:workOrder.predefined'), value: (r) => r.predefinedOrder || '' },
            { header: t('common:tables.headers.concession'), value: (r) => r.concession || '' },
            { header: t('common:tables.headers.road'), value: (r) => r.road || '' },
            { header: t('common:tables.headers.pkInit'), value: (r) => r.pkInit || '' },
            { header: t('common:tables.headers.pkEnd'), value: (r) => r.pkEnd || '' },
            { header: t('common:labels.creator'), value: (r) => r.user || '' },
            { header: t('common:tables.headers.description'), value: (r) => r.description || '' },
            { header: t('common:tables.headers.creationDate'), value: (r) => dateFmt(r.creationDate) },
          ];
        } else if (isSurveillanceRoute) {
          // --- Surveillance Routes (FR-2.5) ---
          // A diferencia del resto de tipos, las filas de la tabla P2 usan el
          // id de la ENTIDAD SurveillanceRoute (no el de su Order) — el detalle
          // navega a /surveillance-route/:srId. Individual → by-id directo.
          let rawRecords: any[];
          if (isIndividual) {
            rawRecords = [];
            setProgress({ current: 0, total: selectedRowIds.length });
            for (let i = 0; i < selectedRowIds.length; i += CHUNK) {
              const chunk = selectedRowIds.slice(i, i + CHUNK);
              const raws = await Promise.all(
                chunk.map(async (srId) => {
                  try {
                    const { data: sd } = await client.query({
                      query: GET_SURVEILLANCE_ROUTE_BY_ID,
                      // El Excel no pinta el aviso de turno: sin él, el
                      // servidor no lee los tramos de cada fila (QA T6).
                      variables: { id: srId, withShiftWarning: false },
                      fetchPolicy: 'cache-first',
                    });
                    return sd?.surveillanceRoute ?? null;
                  } catch {
                    return null;
                  }
                }),
              );
              rawRecords.push(...raws.filter(Boolean));
              setProgress({
                current: Math.min(i + CHUNK, selectedRowIds.length),
                total: selectedRowIds.length,
              });
            }
          } else {
            const result = await client.query({
              query: GET_SURVEILLANCE_ROUTES,
              variables: { input: { ...data.filters, take: 99999, cursor: '' } },
              fetchPolicy: 'no-cache',
            });
            rawRecords = result.data?.surveillanceRoutes?.surveillanceRoutes || [];
          }

          // ROID/descripció viven en la Order paralela; equip/carretera en catálogos.
          const [orderMap, teamsRes, roadsRes] = await Promise.all([
            selectOrdersForSurveillanceRoutes(rawRecords),
            client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
            client.query({ query: GET_ROADS, fetchPolicy: 'cache-first' }),
          ]);
          const teamName = new Map<string, string>(
            (teamsRes.data?.teams || []).map((tm: any) => [tm.id, tm.name]),
          );
          const roadName = new Map<string, string>(
            (roadsRes.data?.roads || []).map((rd: any) => [rd.id, rd.name]),
          );

          resolvedRecords = rawRecords.map((sr: any) => {
            const order = orderMap[sr.id];
            return {
              id: sr.id,
              state: sr.state,
              roid: order?.roid || buildRoidFallback(sr),
              name: sr.name || '',
              registerInitDate: sr.registerInitDate,
              // GAP-1b paridad #72: la fecha fin del RV vive en la Order
              // gemela (la escribe finish()) — ya viene en el orderMap.
              registerEndDate: order?.registerEndDate ?? null,
              team: (sr.teamIDs || [])
                .map((tid: string) => teamName.get(tid))
                .filter(Boolean)
                .join(', '),
              road: (sr.roadIDs || [])
                .map((rid: string) => roadName.get(rid))
                .filter(Boolean)
                // Salto de línea, no coma: así cada carretera queda a la altura
                // de sus PK en las dos columnas de al lado.
                .join('\n'),
              // Multi-carretera (2026-08-17): un solo par de PK es falso con
              // varias carreteras — el escalar es el de la PRIMERA. Un valor
              // por carretera, en el mismo orden que la columna «Carretera» de
              // al lado. Con una sola, la celda queda igual que siempre.
              pkInit: srIsMultiRoad(sr)
                ? resolveRouteRoadRanges(sr)
                    .map((r) => pkFmt(r.pkInitKm, r.pkInitMeter))
                    .join('\n')
                : pkFmt(sr.pkInitKm, sr.pkInitMeter),
              pkEnd: srIsMultiRoad(sr)
                ? resolveRouteRoadRanges(sr)
                    .map((r) => pkFmt(r.pkEndKm, r.pkEndMeter))
                    .join('\n')
                : pkFmt(sr.pkEndKm, sr.pkEndMeter),
              description: order?.description || '',
            };
          });
          setProgress({
            current: resolvedRecords.length,
            total: resolvedRecords.length,
          });

          columns = [
            {
              header: t('common:tables.headers.state'),
              // Mismo mapa que el chip de la tabla, para que el Excel diga
              // lo que el usuario ve en pantalla. Desde 2026-08-05
              // `mapRouteStateToTagVariant` es la identidad y
              // `common:states.in-progress` dice "En progreso" — el mapeo
              // in-progress→work-in-progress que describía este comentario
              // dejó de existir el 2026-07-20.
              value: (r) => translateState(mapRouteStateToTagVariant(r.state)),
            },
            { header: t('common:tables.headers.roid'), value: (r) => roidFmt(r.roid) },
            { header: t('pages:surveillance.name'), value: (r) => r.name || '—' },
            {
              header: t('common:tables.headers.registerInitDate'),
              value: (r) => dateFmt(r.registerInitDate),
            },
            // GAP-1b paridad #72: Data final (Order.registerEndDate del RV).
            { header: t('forms:workOrder.endDate'), value: (r) => dateFmt(r.registerEndDate) },
            { header: t('common:tables.headers.team'), value: (r) => r.team || '' },
            { header: t('common:tables.headers.road'), value: (r) => r.road || '' },
            { header: t('common:tables.headers.pkInit'), value: (r) => r.pkInit || '' },
            { header: t('common:tables.headers.pkEnd'), value: (r) => r.pkEnd || '' },
            { header: t('common:tables.headers.description'), value: (r) => r.description || '' },
          ];
        } else {
          // --- ActionBudgetItems ---
          const result = await client.query({
            query: GET_ACTION_BUDGET_ITEMS_HISTORICAL,
            variables: { input: { ...data.filters, take: 99999, cursor: '' } },
            fetchPolicy: 'no-cache',
          });
          const allRawRecords = result.data?.actionBudgetItemsHistorical?.actionBudgetItems || [];
          // Budget NO tiene N+1 (la query de históricos ya devuelve todo resuelto en
          // UNA petición) y la query by-id no trae `state`, así que aquí se mantiene
          // la lista + filtro por item.id en individual (no hay tormenta que evitar).
          const budgetRecords = isIndividual
            ? allRawRecords.filter((i: any) => selectedRowIds.includes(i?.id))
            : allRawRecords;
          resolvedRecords = budgetRecords.map((item: any) => ({
            id: item.id,
            state: translateState(item.state),
            categoryCode: item.budgetItem?.budgetCategory?.code || '',
            budgetItemCode: item.budgetItem?.code || '',
            description: item.budgetItem?.description || '',
            unit: item.budgetItem?.unit || '',
            unitPrice: item.budgetItem?.unitPrice ?? '',
            quantity: item.quantity ?? '',
            totalAmount: item.totalAmount ?? '',
            roid: item.action?.roid || '',
            registrationDate: item.createdAt || '',
            pkInit: pkFmt(item.action?.pkInitKm, item.action?.pkInitMeter),
            pkEnd: pkFmt(item.action?.pkEndKm, item.action?.pkEndMeter),
            concession: item.action?.concessions?.[0]?.name || '',
            externalCompanies: (item.action?.externalCompanies || []).join(', '),
          }));
          setProgress({
            current: resolvedRecords.length,
            total: resolvedRecords.length,
          });

          columns = [
            // `state` ya viene traducido del map de arriba (translateState).
            { header: t('common:tables.headers.state'), value: (r) => r.state },
            { header: t('budgets:columns.categoryCode'), value: (r) => r.categoryCode },
            { header: t('budgets:columns.budgetItemCode'), value: (r) => r.budgetItemCode },
            { header: t('common:tables.headers.description'), value: (r) => r.description },
            { header: t('budgets:columns.unit'), value: (r) => r.unit },
            { header: t('budgets:columns.unitPrice'), value: (r) => String(r.unitPrice) },
            { header: t('budgets:columns.quantity'), value: (r) => String(r.quantity) },
            { header: t('budgets:columns.totalAmount'), value: (r) => String(r.totalAmount) },
            { header: t('budgets:columns.roid'), value: (r) => r.roid },
            {
              header: t('budgets:columns.registrationDate'),
              value: (r) => dateFmt(r.registrationDate),
            },
            { header: t('common:tables.headers.pkInit'), value: (r) => r.pkInit },
            { header: t('common:tables.headers.pkEnd'), value: (r) => r.pkEnd },
            { header: t('common:tables.headers.concession'), value: (r) => r.concession },
            {
              header: t('common:tables.headers.externalCompanies'),
              value: (r) => r.externalCompanies,
            },
          ];
        }

        console.log(`[MODAL] Fetched and resolved ${resolvedRecords.length} records`);

        // Filter: selectAll uses excludedIds, individual uses selectedRows
        let filtered: any[];
        if (data.selectAll) {
          const excludedSet = new Set(data.excludedIds || []);
          filtered = resolvedRecords.filter((r: any) => !excludedSet.has(r.id));
        } else {
          const selectedSet = new Set(data.selectedRows.filter(Boolean));
          filtered = resolvedRecords.filter((r: any) => selectedSet.has(r.id));
        }
        console.log(`[MODAL] Filtered to ${filtered.length} records for Excel`);

        // Cabeceras y filas salen de la MISMA definición → no pueden desalinearse.
        const headers = columns.map((c) => c.header);
        const filteredRows = filtered.map((r: any) => columns.map((c) => c.value(r) ?? ''));

        await generateExcelFromData(headers, filteredRows, {
          filename: `historical-${data.type}.xlsx`,
        });
        console.log(`[MODAL] Excel generated with ${filteredRows.length} rows`);
      } catch (error) {
        console.error('[MODAL] Error in data-based export:', error);
        await tableToExcelRef.current?.download();
      }
    } else if (tableToExcelRef.current) {
      console.log('[MODAL] Using DOM-BASED fallback export');
      await tableToExcelRef.current.download();
    } else {
      console.log('[MODAL] TableToExcel not initialized yet');
    }
  }, [data.selectAll, data.selectedRows, data.filters, data.excludedIds, data.type]);

  const downloadPDF = useCallback(async () => {
    const isAccident = data.type === 'Accident' || data.type === 'accident';
    const isAction = data.type === 'Action' || data.type === 'action';

    // For Actions and Accidents: generate detailed rich PDF with summaries + images
    if ((isAction || isAccident) && data.filters) {
      try {
        log('Generating detailed report PDF...');
        // 1. Get the order IDs to include
        let orderIds: string[];
        if (data.selectAll) {
          // Fetch all IDs with filters
          const query = isAccident ? FETCH_ACCIDENTS : FETCH_ACTIONS;
          const key = isAccident ? 'accidents' : 'actions';
          const result = await client.query({
            query,
            variables: { input: { ...data.filters, take: 99999, cursor: '' } },
            fetchPolicy: 'no-cache',
          });
          const allRawRecords = result.data?.[key]?.[key] || [];
          const selector = isAccident ? selectAccidentOrders : selectActionOrders;
          const resolved = await selector(allRawRecords);
          const excludedSet = new Set(data.excludedIds || []);
          orderIds = resolved
            .filter((r: any) => !excludedSet.has(r.id))
            .map((r: any) => r.id);
        } else {
          orderIds = data.selectedRows.filter(Boolean) as string[];
        }

        log(`Fetching detailed data for ${orderIds.length} records...`);

        // 2. Fetch detailed data for each record
        const records = await fetchDetailedReportData({
          orderIds,
          type: isAction ? 'Action' : 'Accident',
          cloudName: import.meta.env.VITE_CLOUD_NAME || '',
          onProgress: (current, total) => setProgress({ current, total }),
        });

        log(`Fetched ${records.length} detailed records, generating PDF...`);

        // 3. Build filter summary from data.filters
        const filterItems: { label: string; value: string }[] = [];
        const filters = data.filters || {};

        // Date range — format ISO dates to readable format using date-fns
        const fmtFilterDate = (isoDate: string): string => {
          try {
            const d = new Date(isoDate);
            if (isNaN(d.getTime())) return isoDate;
            return format(d, 'dd/MM/yyyy');
          } catch { return isoDate; }
        };
        if (filters.fromDate || filters.toDate) {
          const from = filters.fromDate ? fmtFilterDate(filters.fromDate) : '—';
          const to = filters.toDate ? fmtFilterDate(filters.toDate) : '—';
          filterItems.push({ label: String(t('tables:headers.date', 'Data')), value: `${from} — ${to}` });
        }
        // Resolve filter names from Apollo cache
        if (filters.concessionId) {
          try {
            const { data: d } = await client.query({ query: GET_CONCESSION_BY_ID, variables: { id: filters.concessionId }, fetchPolicy: 'cache-first' });
            if (d?.concession?.name) filterItems.push({ label: t('tables:headers.concession'), value: d.concession.name });
          } catch { /* skip */ }
        }
        if (filters.predefinedOrderId) {
          try {
            const cached = client.readQuery({ query: FETCH_PREDEFINED_ORDERS });
            const pred = cached?.predefinedOrders?.find((p: any) => p.id === filters.predefinedOrderId);
            if (pred?.name) filterItems.push({ label: t('forms:workOrder.predefined'), value: pred.name });
          } catch { /* skip */ }
        }
        if (filters.state) {
          filterItems.push({ label: String(t('tables:headers.status', 'Estat')), value: String(t(`common:states.${filters.state}`, filters.state)) });
        }
        if (filters.classification) {
          filterItems.push({ label: t('tables:headers.classification', 'Classificació'), value: filters.classification });
        }
        const filtersSummary = filterItems.length > 0 ? filterItems : undefined;

        // Locale for date formatting
        const localeMap: Record<string, string> = { ca: 'ca-ES', es: 'es-ES', en: 'en-GB' };
        const dateLocale = localeMap[i18n.language] || 'ca-ES';

        // 4. Generate PDF
        const reportData = {
          title: isAction
            ? t('modals:downloadHistoric.reportTitleActions', { defaultValue: "Informe d'Actuacions" })
            : t('modals:downloadHistoric.reportTitleAccidents', { defaultValue: "Informe d'Accidents" }),
          orderType: isAction ? 'Action' : 'Accident',
          records,
          generatedAt: new Date().toLocaleDateString(dateLocale, {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
          }),
          totalCount: records.length,
          filtersSummary,
        };

        await generateDetailedPDF(reportData, `informe-${data.type}.pdf`);
        log('Detailed PDF generated successfully');
      } catch (error) {
        console.error('Error generating detailed PDF:', error);
        // Fallback to simple PDF
        if (pdfDataRef.current) {
          await generateHistoricalPDF(pdfDataRef.current, `historical-${data.type}.pdf`);
        }
      }
    } else if (pdfDataRef.current) {
      await generateHistoricalPDF(
        pdfDataRef.current,
        `historical-${data.type}.pdf`
      );
    } else if (tableToPdfRef.current) {
      await tableToPdfRef.current.download();
    } else {
      log('PDF not initialized yet');
    }
  }, [data.type, data.selectAll, data.selectedRows, data.filters, data.excludedIds]);

  // Informe combinado "con todo": orden padre + hijas (actuaciones, accidentes,
  // comunicaciones) con fotos. 1 orden → PDF; varias → ZIP de PDFs.
  const downloadCombined = useCallback(async () => {
    if (!data.entityType) return;
    try {
      log('Generando informe combinado...');
      let entityIds: string[];
      if (data.selectAll) {
        const excludedSet = new Set(data.excludedIds || []);
        if (data.entityType === 'surveillanceRoute') {
          // RV: al revés que genéricas/campañas, las filas del histórico
          // publican el id de la ENTIDAD SurveillanceRoute (sr.id) y
          // `fetchSurveillanceReportData` espera ese mismo id (resuelve la
          // Order doble internamente) — espejo de la rama Excel del RV.
          const result = await client.query({
            query: GET_SURVEILLANCE_ROUTES,
            variables: { input: { ...data.filters, take: 99999, cursor: '' } },
            fetchPolicy: 'no-cache',
          });
          const all =
            result.data?.surveillanceRoutes?.surveillanceRoutes || [];
          entityIds = all
            .map((sr: { id: string }) => sr.id)
            .filter((id: string) => !excludedSet.has(id));
        } else {
          const query =
            data.entityType === 'campaign' ? GET_CAMPAIGNS : GET_GENERIC_ORDERS;
          const key = data.entityType === 'campaign' ? 'campaigns' : 'genericOrders';
          const result = await client.query({
            query,
            variables: { input: { ...data.filters, take: 99999, cursor: '' } },
            fetchPolicy: 'no-cache',
          });
          const raw = result.data?.[key]?.[key] || [];
          // El id de cada fila (y de selectedRows) es el id del Order PADRE, no el
          // de la entidad GenericOrder/Campaign. El raw trae la entidad → usamos
          // orderIDs[0] (entidad doble: 1 Order por GenericOrder/Campaign) para
          // obtener el Order padre que fetchCombinedReportData espera vía
          // GET_ORDER_BY_ID. Antes se usaba r.id (entidad) → GET_ORDER_BY_ID fallaba,
          // el ZIP salía vacío (22 B) y excludedIds (ids de Order) no filtraba nada.
          entityIds = raw
            .map((r: { orderIDs?: string[] }) => r.orderIDs?.[0] ?? '')
            .filter((id: string) => id !== '' && !excludedSet.has(id));
        }
      } else {
        entityIds = data.selectedRows.filter(Boolean) as string[];
      }

      if (entityIds.length === 0) {
        log('No hay órdenes seleccionadas para el informe combinado');
        return;
      }

      // El Recorregut de Vigilància tiene documento propio con opciones por
      // niveles (2026-07-27): ficha + tramos por defecto, y hasta el detalle
      // completo de las actuaciones si se marca. El combinado ya no lo trata.
      if (data.entityType === 'surveillanceRoute') {
        await generateSurveillancePDF({
          routeIds: entityIds,
          options: srOptions,
          cloudName: import.meta.env.VITE_CLOUD_NAME || '',
          sort: reportSort,
          onProgress: (current, total) => setProgress({ current, total }),
        });
        log('Informe del recorrido generado correctamente');
        return;
      }

      await generateCombinedPDF({
        entityIds,
        entityType: data.entityType,
        cloudName: import.meta.env.VITE_CLOUD_NAME || '',
        sort: reportSort,
        onProgress: (current, total) => setProgress({ current, total }),
      });
      log('Informe combinado generado correctamente');
    } catch (error) {
      console.error('Error generando informe combinado:', error);
    }
  }, [
    data.entityType,
    data.selectAll,
    data.selectedRows,
    data.filters,
    data.excludedIds,
    reportSort,
    srOptions,
  ]);

  return (
    <ModalContainer width={45} onClickClose={onClickClose} fill={theme.colors.black}>
      <Container {...props}>
        <Icon src={DownloadIcon}></Icon>
        <Title size="XL" variant="semiBold">
          {data.showReport || data.showCombinedReport
            ? t('downloadHistoric.titleWithReport', {
                defaultValue: t('downloadHistoric.title'),
              })
            : t('downloadHistoric.title')}
        </Title>
        {generating ? (
          <GeneratingBox>
            <Loader />
            <GeneratingText size="L" variant="medium">
              {t('toasts:info.processing')}
              {progress ? ` ${progress.current}/${progress.total}` : ''}
            </GeneratingText>
          </GeneratingBox>
        ) : (
          <>
            {isSurveillanceReport && (
              <SurveillanceReportOptions
                value={srOptions}
                onChange={setSrOptions}
              />
            )}
            {showSortControl && (
              <SortControl
                role="group"
                aria-label={t('downloadHistoric.sortLabel')}
              >
                <SortOptionsRow>
                  {(
                    [
                      {
                        field: 'registerInitDate',
                        label: t('common:tables.headers.registerInitDate'),
                      },
                      {
                        field: 'creationDate',
                        label: t('common:tables.headers.creationDate'),
                      },
                    ] as { field: ReportSortField; label: string }[]
                  ).map(({ field, label }) => {
                    const active = reportSort.field === field;
                    return (
                      <SortOptionButton
                        key={field}
                        type="button"
                        aria-pressed={active}
                        data-direction={
                          active ? reportSort.direction : undefined
                        }
                        onClick={() => onToggleReportSort(field)}
                      >
                        <Typography
                          size="S"
                          variant={active ? 'semiBold' : 'regular'}
                          component="span"
                        >
                          {label}
                        </Typography>
                        <SortArrow
                          $asc={active && reportSort.direction === 'asc'}
                          $active={active}
                          aria-hidden="true"
                        >
                          <ArrowBottom />
                        </SortArrow>
                      </SortOptionButton>
                    );
                  })}
                </SortOptionsRow>
                <SortHint size="XS" colorText="greyDark">
                  {t('downloadHistoric.sortHint')}
                </SortHint>
              </SortControl>
            )}
            <ButtonsRow>
              <Button
                onClick={() => runWithSpinner(downloadHistoric)}
                text={t('downloadHistoric.downloadButton')}
              ></Button>
              {data.showReport && (
                <Button
                  onClick={() => runWithSpinner(downloadPDF)}
                  text={t('downloadHistoric.reportButton', {
                    defaultValue: 'Informe',
                  })}
                ></Button>
              )}
              {data.showCombinedReport && (
                <Button
                  onClick={() => runWithSpinner(downloadCombined)}
                  text={
                    isSurveillanceReport
                      ? t('surveillanceReport.generateButton')
                      : t('downloadHistoric.combinedButton')
                  }
                ></Button>
              )}
            </ButtonsRow>
          </>
        )}
      </Container>
    </ModalContainer>
  );
};
